import './glocke.css';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { dayLabel, fmtTime } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { requestPath } from '../anfragen/labels';
import { letterPath } from '../briefe/labels';
import { chatTabPath } from '../chat/data';

// Glocke im Kanzlei-Dashboard (oben rechts): neue Anfragen, Briefe, Belege, Mitarbeiter-Meldungen, Chat-Nachrichten,
// Registrierungen, unterschriebene Dokumente und (Admin) „Rechnung schreiben“. Die Meldungen gehen an den Zuständigen des Mandanten (FiBu bzw.
// Lohn), ist niemand zugewiesen, an den Admin. Kommt live etwas dazu, gibt es eine kurze Meldung unten am Bildschirm.

interface StaffNote {
  id: string;
  clientId: string;
  clientName: string;
  type: string;
  title: string;
  body: string | null;
  data: Record<string, string | number>;
  createdAt: string;
  read: boolean;
}

interface NotesResponse {
  unread: number;
  items: StaffNote[];
}

const ICON: Record<string, string> = {
  k_anfrage: '📄',
  k_brief: '✉️',
  k_belege: '📎',
  k_mitarbeiter: '👤',
  k_aenderung: '✏️',
  k_kuendigung: '📅',
  k_registrierung: '🆕',
  k_rechnung: '🧾',
  k_unterschrift: '✍️',
  k_freigabe: '✅',
};
const icon = (type: string) => (type.endsWith('_comment') || type === 'k_chat' ? '💬' : (ICON[type] ?? '🔔'));

// Wohin der Klick führt
export function noteTarget(n: StaffNote): string | null {
  const d = n.data;
  const c = n.clientId;
  if (n.type.startsWith('k_anfrage') && typeof d.requestId === 'string') return requestPath(c, d.requestId);
  if (n.type.startsWith('k_brief') && typeof d.letterId === 'string') return letterPath(c, d.letterId);
  if ((n.type === 'k_belege' || n.type === 'k_beleg_comment') && d.year && d.month) {
    const q = `jahr=${d.year}&monat=${d.month}${n.type === 'k_beleg_comment' ? `&beleg=${d.belegId}` : ''}`;
    return `/kanzlei/mandanten/${c}/belege?${q}`;
  }
  if (['k_mitarbeiter', 'k_aenderung', 'k_kuendigung'].includes(n.type) && typeof d.employeeId === 'string') {
    return `/kanzlei/mandanten/${c}/lohn?ma=${d.employeeId}`;
  }
  if (n.type === 'k_chat') return chatTabPath(c);
  if (n.type === 'k_registrierung') return `/kanzlei/registrierungen/${c}`;
  if (n.type === 'k_rechnung') return '/kanzlei/abrechnung';
  if (n.type === 'k_unterschrift') return `/kanzlei/mandanten/${c}/hochladen`;
  if (n.type === 'k_freigabe') return `/kanzlei/mandanten/${c}/lohn`;
  return null;
}

const when = (v: string) => `${dayLabel(v)} ${fmtTime(v)}`;
// Gesammelte Meldungen (Belege eines Monats, Chat) behalten ihre ID und kommen mit neuer Zeit wieder nach oben
const seenKey = (n: StaffNote) => `${n.id}|${n.createdAt}`;

export function Glocke() {
  const toast = useToast();
  const navigate = useNavigate();
  const res = useApi<NotesResponse>('/api/kanzlei/notifications', { live: ['notifications'] });
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const unread = res.data?.unread ?? 0;

  // Meldung nur für Einträge, die nach dem ersten Laden dazukommen
  const known = useRef<Set<string> | null>(null);
  useEffect(() => {
    const items = res.data?.items;
    if (!items) return;
    if (!known.current) {
      known.current = new Set(items.map(seenKey));
      return;
    }
    const fresh = items.filter((i) => !i.read && !known.current!.has(seenKey(i)));
    for (const i of items) known.current.add(seenKey(i));
    const top = fresh[0];
    if (top) toast(`${top.title} · ${top.clientName}`);
  }, [res.data, toast]);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  const go = async (n: StaffNote) => {
    setOpen(false);
    if (!n.read) {
      await api.post(`/api/kanzlei/notifications/${n.id}/read`).catch(() => {});
      // Kommentar zum Beleg: wie „Antworten“ in der Leiste quittieren
      if (n.type === 'k_beleg_comment') await api.post(`/api/kanzlei/beleg/${n.data.belegId}/ack`).catch(() => {});
      void res.reload();
    }
    const target = noteTarget(n);
    if (target) navigate(target);
  };

  const readAll = async () => {
    await api.post('/api/kanzlei/notifications/read-all').catch(() => {});
    void res.reload();
  };

  const items = res.data?.items ?? [];
  return (
    <div className="kbell" ref={ref}>
      <button
        type="button"
        className="kbell-btn"
        aria-label={unread ? `Benachrichtigungen, ${unread} neu` : 'Benachrichtigungen'}
        aria-expanded={open}
        onClick={() => {
          setOpen((o) => !o);
          void res.reload();
        }}
      >
        🔔
        {unread > 0 && (
          <span className="kbell-n" aria-hidden="true">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="kbell-menu" role="dialog" aria-label="Benachrichtigungen">
          <div className="kbell-head">
            <b>Benachrichtigungen</b>
            <span>{unread ? `${unread} neu` : 'alles gelesen'}</span>
            {unread > 0 && (
              <button type="button" className="linkbtn" onClick={() => void readAll()}>
                Alle als gelesen
              </button>
            )}
          </div>
          <div className="kbell-list">
            {items.length ? (
              items.map((n) => (
                <button key={n.id} type="button" className={`kbell-r${n.read ? ' read' : ''}`} onClick={() => void go(n)}>
                  <span className="kbell-i" aria-hidden="true">
                    {icon(n.type)}
                  </span>
                  <span className="kbell-tx">
                    <b>{n.title}</b>
                    <small>
                      {n.clientName}
                      {n.body ? ` · ${n.body}` : ''}
                    </small>
                    <small className="kbell-t">
                      {when(n.createdAt)}
                      {!n.read && <span className="pill p-info">neu</span>}
                    </small>
                  </span>
                </button>
              ))
            ) : (
              <div className="kbell-empty">Noch keine Benachrichtigungen.</div>
            )}
          </div>
          <div className="kbell-foot">Neues von Ihren Mandanten: Anfragen, Briefe, Belege, Mitarbeiter und Chat.</div>
        </div>
      )}
    </div>
  );
}
